import i18next from 'i18next';
import { notSupportedStepTypesForMuscles } from 'utils/workout/MuscleMapUtil';
import Types from 'pages/workouts/util/Types';
i18next.loadNamespaces(['workout', 'activity_types']);

export const getWorkoutName = (workout, isWheelchairMode = false) => {
    let workoutName = workout.workoutName;
    if (!workout.workoutName) {
        if (workout.sportType && workout.sportType.sportTypeKey) {
            workoutName = i18next.t('workout:workout.default.title.' + workout.sportType.sportTypeKey);

            if (isWheelchairMode) {
                if (workout.sportType.sportTypeKey === Types.SportTypes.Running) {
                    workoutName = i18next.t('workout:workout.default.title.push.run.speed');
                }

                if (workout.sportType.sportTypeKey === Types.SportTypes.Cycling) {
                    workoutName = i18next.t('workout:workout.default.title.handcycling');
                }
            }
        } else {
            workoutName = i18next.t('workout:workout_label');
        }
    }
    return workoutName;
};

export const parseWorkoutTypes = (apiSportTypes, bannedWorkoutTypes) => {
    if (!apiSportTypes || apiSportTypes.length === 0) {
        return [];
    }

    let sportTypes = apiSportTypes.filter((sportType) => {
        return !bannedWorkoutTypes.includes(sportType.sportTypeKey);
    });

    sportTypes = sportTypes.map((sportType) => {
        let nameKey = `activity_type_${sportType.sportTypeKey}`;
        let navigationUrl = `/workout/create/${sportType.sportTypeKey}`;
        switch (sportType.sportTypeKey) {
            case 'running':
                nameKey = 'workout.run';
                break;
            case 'cycling':
                nameKey = 'workout.bike';
                break;
            case 'other':
                nameKey = 'workout.activity.type.other';
                break;
            case 'lap_swimming':
                // rename lap_swimming to swimming
                sportType.sportTypeKey = 'swimming';
                nameKey = 'activity_type_lap_swimming';
                navigationUrl = '/workout/create/swimming';
                break;
            case 'swimming':
                nameKey = 'activity_type_lap_swimming';
                break;
            case 'strength_training':
                nameKey = 'workout.strength';
                break;
            case 'cardio_training':
                nameKey = 'workout.cardio';
                break;
            case 'yoga':
                nameKey = 'workout.yoga';
                break;
            case 'pilates':
                nameKey = 'workout.pilates';
                break;
            case 'mobility':
                nameKey = 'workout.mobility';
                break;
            case 'multi_sport':
                nameKey = 'workout.multi.sport';
                break;
            default:
                break;
        }

        return {
            ...sportType,
            nameKey,
            navigationUrl,
            value: sportType.sportTypeKey
        };
    });

    // sort by displayOrder
    sportTypes = sportTypes.sort((a, b) => a.displayOrder - b.displayOrder);

    return sportTypes;
};

export const extractExercises = (workoutSegments) => {
    const exercisesArray = [];

    function recursivelyExtractExercises(workoutSteps) {
        for (var s = 0, l = workoutSteps.length; s < l; s += 1) {
            if (!workoutSteps[s]) {
                continue;
            }

            const { exerciseKey, categoryKey, stepType } = workoutSteps[s];

            if (!notSupportedStepTypesForMuscles.includes(stepType.stepTypeKey)) {
                exercisesArray.push({ exerciseKey, categoryKey });
            }

            if (workoutSteps[s].workoutSteps && workoutSteps[s].workoutSteps.length > 0) {
                recursivelyExtractExercises(workoutSteps[s].workoutSteps);
            }
        }
    }

    if (workoutSegments && workoutSegments.length > 0) {
        for (var i = 0, len = workoutSegments.length; i < len; i += 1) {
            var workoutSteps = workoutSegments[i].workoutSteps;
            if (workoutSteps) {
                recursivelyExtractExercises(workoutSteps);
            }
        }
    }

    return exercisesArray;
};

function recursivelyVerifyWorkoutSteps(workoutSteps, functionToApply) {
    if (!functionToApply || typeof functionToApply !== 'function') {
        return;
    }

    let conditionIsMet = false;

    for (var s = 0, l = workoutSteps.length; s < l; s += 1) {
        if (workoutSteps[s] && functionToApply(workoutSteps[s])) {
            return true;
        }

        if (workoutSteps[s].workoutSteps && workoutSteps[s].workoutSteps.length > 0) {
            conditionIsMet =
                conditionIsMet || recursivelyVerifyWorkoutSteps(workoutSteps[s].workoutSteps, functionToApply);
        }
    }

    return conditionIsMet;
}

export const workoutHasStepsThatMeetFunctionCondition = (workoutSegments, functionToApply) => {
    let conditionIsMet = false;

    if (workoutSegments && workoutSegments.length > 0) {
        for (var i = 0, len = workoutSegments.length; i < len; i += 1) {
            var workoutSteps = workoutSegments[i].workoutSteps;
            if (workoutSteps) {
                conditionIsMet = conditionIsMet || recursivelyVerifyWorkoutSteps(workoutSteps, functionToApply);
            }
        }
    }

    return conditionIsMet;
};

export const parseWorkoutSegments = (workoutSegments, stepFunctionToApply) => {
    function recursivelyCheckWorkoutSteps(workoutSteps) {
        for (let s = 0, l = workoutSteps.length; s < l; s += 1) {
            if (workoutSteps[s]) {
                if (stepFunctionToApply && typeof stepFunctionToApply === 'function') {
                    stepFunctionToApply(workoutSteps[s]);
                }
                convertToCategoryAndExerciseKey(workoutSteps[s]);
            }

            if (workoutSteps[s].workoutSteps && workoutSteps[s].workoutSteps.length > 0) {
                recursivelyCheckWorkoutSteps(workoutSteps[s].workoutSteps);
            }
        }
    }

    if (workoutSegments && workoutSegments.length > 0) {
        for (let i = 0, len = workoutSegments.length; i < len; i += 1) {
            const workoutSteps = workoutSegments[i].workoutSteps;
            if (workoutSteps) {
                recursivelyCheckWorkoutSteps(workoutSteps);
            }
        }
    }

    return workoutSegments;
};

export const convertToCategoryAndExerciseKey = (workoutStep) => {
    // for each workout step, transform the response to have categoryKey and exerciseKey
    // these fields are used throughout GCW instead of category & exerciseName
    const { category, exerciseName } = workoutStep;

    workoutStep.categoryKey = category;
    workoutStep.exerciseKey = exerciseName ? exerciseName : category;
    delete workoutStep.category;
    delete workoutStep.exerciseName;
};

export const convertFromCategoryAndExerciseKey = (workoutStep) => {
    const { categoryKey, exerciseKey } = workoutStep;

    // for each workout step, transform the payload to have the fields expected by the API:
    // category & exerciseName instead of categoryKey & exerciseKey
    workoutStep.category = categoryKey;
    workoutStep.exerciseName = exerciseKey !== categoryKey ? exerciseKey : null;
    delete workoutStep.categoryKey;
    delete workoutStep.exerciseKey;
};

export const getSportTypes = (sportTypes) => {
    let sportTypesString = '';
    const availableSports = {
        running: i18next.t('activity_types:activity_type_running'),
        cycling: i18next.t('activity_types:activity_type_cycling'),
        swimming: i18next.t('activity_types:activity_type_swimming'),
        mountain_biking: i18next.t('activity_types:activity_type_mountain_biking'),
        snow_skiing: i18next.t('activity_types:activity_type_snow_skiing'),
        snowboarding: i18next.t('activity_types:activity_type_snowboarding'),
        surfing: i18next.t('activity_types:activity_type_surfing'),
        hiking: i18next.t('activity_types:activity_type_hiking'),
        paddle_sports: i18next.t('activity_types:activity_type_paddle_sports'),
        racquet_sports: i18next.t('activity_types:activity_type_racquet_sports'),
        golf: i18next.t('activity_types:activity_type_golf')
    };

    sportTypesString = sportTypes
        .map((sport) => availableSports[sport])
        .sort()
        .join(', ');

    return sportTypesString;
};
